import React, { useState, useEffect, useRef } from 'react';

// Import icons
import lettingsIcon from "../../images/product_bar/Lettings.png";
import listingsIcon from "../../images/product_bar/Listing.png";
import refurbishmentIcon from "../../images/product_bar/Refurbishment.png";
import brokerageIcon from "../../images/product_bar/Brokerage.png";
import legalsIcon from "../../images/product_bar/Legals.png";
import useThemeStore from "../../store/themeStore";
import FeatureEnquiryModal from "../components/FeatureEnquiryModal";
import { ProductIds } from "../components/FeaturedCardsCarousel";

// Import images
import lettingsImage from "../../images/carousel/Carousel_Lettings.jpg";
import listingsImage from "../../images/carousel/Carousel_Listings.png";
import refurbishmentImage from "../../images/carousel/Carousel_Refurbishment.png";
import brokerageImage from "../../images/carousel/Carousel_Brokerage.png";
import legalsImage from "../../images/carousel/Carousel_Legal.jpg";

const serviceIcons = {
  lettingsIcon,
  listingsIcon,
  refurbishmentIcon,
  brokerageIcon,
  legalsIcon
};

const serviceImages = {
  lettingsImage,
  listingsImage,
  refurbishmentImage,
  brokerageImage,
  legalsImage
};

const PropertyServicesCarousel = () => {
  const [activeService, setActiveService] = useState(0); // Start with the first slide (index 0)
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // States for feature enquiry modal
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  
  // Get theme colors from store
  const getThemes = useThemeStore((state) => state.theme);
  
  // Get theme color 2 or default to blue if not available
  const themeColor2 = getThemes?.color2 || "#3B82F6";
  const textColor = getThemes?.txtColor || "#000000";
  
  // Function to check if a color is white or a shade of white
   // White
   const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
  
    // 1) explicit white checks
    if (
      s === "white" ||
      s === "#fff" ||
      s === "#ffffff" ||
      s === "rgb(255,255,255)" ||
      s === "rgba(255,255,255,1)"
    ) {
      return true;
    }
  
    let r: number, g: number, b: number;
  
    // 2) hex formats
    if (s.startsWith("#")) {
      let hex = s.slice(1);
      if (hex.length === 3) {
        // expand `#abc` → `#aabbcc`
        hex = hex.split("").map(ch => ch + ch).join("");
      }
      if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
        return false;
      }
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
    // 3) rgb/rgba formats
    else {
      const m = s.match(
        /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/
      );
      if (!m) return false;
      r = Number(m[1]);
      g = Number(m[2]);
      b = Number(m[3]);
    }
  
    // 4) threshold for "almost white"
    return r >= 235 && g >= 235 && b >= 235;
  };
  

  
  // Service data for the carousel
  const services = [
    { 
      name: 'Lettings',
      icon: serviceIcons.lettingsIcon,
      image: serviceImages.lettingsImage,
      title: "Lettings",
      description: "Leave your property in trusted hands! With Our Efficient National Lettings Agency, our experienced team works tirelessly to protect your investment and maximise your rental income.",
      productId: ProductIds.lettings
    },
    { 
      name: 'Listings',
      icon: serviceIcons.listingsIcon, 
      image: serviceImages.listingsImage,
      title: "Listings",
      description: "Selling your property? Gain immediate access to a vast network of active home buyers and property investors through our off-market estate agency.",
      productId: ProductIds.listings
    },
    { 
      name: 'Legals',
      icon: serviceIcons.legalsIcon,
      image: serviceImages.legalsImage,
      title: "Legals & Conveyancing",
      description: "Benefit from low-cost conveyancing fees through our panel of solicitors. We offer a range of services designed to make your property transactions smooth and hassle-free.",
      productId: ProductIds.legals
    },
    { 
      name: 'Refurbishment',
      icon: serviceIcons.refurbishmentIcon,
      image: serviceImages.refurbishmentImage,
      title: "Refurbishment",
      description: "We will Repair, Improve and Maintain Your Property. We specialise in property transformation and repairs.",
      productId: ProductIds.refurbishment
    },
    { 
      name: 'Brokerage',
      icon: serviceIcons.brokerageIcon,
      image: serviceImages.brokerageImage,
      title: "Brokerage",
      description: "We offer tailored mortgage solutions with a friendly, professional touch. Whether you're a first-time buyer or expanding your property portfolio.",
      productId: ProductIds.brokerage
    },
  ];

  useEffect(() => {
    if (isAutoSliding && !isFeatureModalOpen) {
      timerRef.current = setInterval(() => {
        setActiveService((prev) => (prev + 1) % services.length);
      }, 5000); // 10 seconds in milliseconds
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isAutoSliding, services.length, isFeatureModalOpen]);

  const handleServiceClick = (index: number) => {
    setActiveService(index);
    setIsAutoSliding(false);
    
    // Restart auto-sliding after 1 minute of inactivity
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setTimeout(() => {
      setIsAutoSliding(true);
    }, 60000); // 1 minute in milliseconds
  };

  // Function to open the FeatureEnquiryModal with the correct productId
  const handleEnquire = (productId: string, e: React.MouseEvent) => {
    // Prevent propagation to parent elements
    e.stopPropagation();
    setSelectedProductId(productId);
    setIsFeatureModalOpen(true);
  };

  // Inline style to enforce white text regardless of parent container
  const whiteTextStyle = {
    color: '#ffffff',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
    maxWidth: '100%',
    overflowWrap: 'break-word' as const,
    wordWrap: 'break-word' as const
  };
  
  return (
    <div className="property-services-carousel w-full">
      {/* Add a custom style tag to ensure white text with !important flags */}
      <style>
        {`
          :root {
            --theme-color-2: ${themeColor2};
          }
          .property-services-carousel h3,
          .property-services-carousel p,
          .property-services-carousel div.text-white {
            color: #000000 !important;
          }
          
          /* Exclude top icon text from being forced white */
          .property-services-carousel .icon-text {
            color: inherit;
            text-shadow: none;
          }
          
          .property-services-carousel .service-title {
            color: #ffffff !important;
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
            font-weight: bold;
          }
          
          .property-services-carousel .service-description {
            color: #ffffff !important;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
            overflow-wrap: break-word !important;
            word-break: break-word !important;
            max-width: 100% !important;
            hyphens: auto !important;
            box-sizing: border-box !important;
            padding-right: 8px !important;
          }
          
          /* Utility classes for text truncation on mobile */
          .line-clamp-4 {
            display: -webkit-box !important;
            -webkit-line-clamp: 4 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          
          .line-clamp-none {
            -webkit-line-clamp: unset !important;
            display: block !important;
          }
          
          /* Fix for mobile text container */
          @media (max-width: 640px) {
            .property-services-carousel .service-description {
              width: calc(100vw - 40px) !important;
              max-width: calc(100vw - 40px) !important;
            }
          }
          
          .property-services-carousel .collapsed-name {
            color: #ffffff !important;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
          }
          
          /* This is the overlay that darkens the image backgrounds - you can adjust the color and opacity here */
          .carousel-overlay {
            background-color: rgba(0, 0, 0, 0.25);
            position: absolute;
            inset: 0;
            z-index: 1;
          }

          /* Ensure navigation dots are not affected by white text styles */
          .property-services-carousel button.w-3 {
            color: inherit !important;
            text-shadow: none !important;
          }
          
          /* Style for the icon container with theme color 2 background */
          .icon-circle-theme {
            background-color: var(--theme-color-2);
            border: 2px solid var(--theme-color-2); 
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          .property-services-carousel .nav-dot-active {
            background-color: var(--theme-color-2) !important; 
          }
                
          /* Ensure navigation dots are not affected by white text styles */
          .property-services-carousel button.w-3 {
            color: inherit !important;
            text-shadow: none !important;
          }
          
          /* Mobile carousel navigation handles styles */
          .mobile-nav-handle {
            background-color: rgba(0, 0, 0, 0.6) !important;
            color: white !important;
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.5), 0 0 5px rgba(255, 255, 255, 0.2);
            transition: all 0.2s ease;
            padding: 10px !important;
            border-radius: 8px !important;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .mobile-nav-handle:hover {
            background-color: rgba(0, 0, 0, 0.8) !important;
            transform: scale(1.05);
            box-shadow: 0 0 15px rgba(0, 0, 0, 0.7), 0 0 8px rgba(255, 255, 255, 0.3);
          }
          
          .mobile-nav-handle svg {
            stroke-width: 3;
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
          }
        `}
      </style>
      
      <div className="max-w-7xl mx-auto py-4 sm:py-8 px-2 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-8">End-To-End Property Solutions</h2>
        
        {/* Service Icons Navigation */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 sm:gap-2 mb-4 sm:mb-8">
          {services.map((service, idx) => (
            <div 
              key={idx}
              onClick={() => handleServiceClick(idx)}
              className="flex flex-col items-center cursor-pointer"
            >
              <div className={`w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 flex items-center justify-center rounded-lg mb-1 sm:mb-2 transition-colors duration-200 ${
                idx === activeService ? 'bg-gray-200' : ''
              }`} 
              style={{
                background: idx === activeService ? themeColor2 : '#f3f4f6',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
              }}>
                <img 
                  src={service.icon} 
                  alt={service.name} 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                  style={{ 
                    filter: `brightness(0) saturate(100%) ${idx === activeService ? 'invert(1)' : ''}`,
                    color: textColor
                  }}
                />
              </div>
              <span className={`text-[10px] sm:text-xs text-center icon-text ${
                idx === activeService ? 'text-white' : 'text-gray-700'
              }`}>{service.name}</span>
            </div>
          ))}
        </div>
        
        {/* Main Carousel with Side Sections */}
        <div className="flex h-[20rem] sm:h-[32rem] space-x-1 sm:space-x-2 rounded-2xl sm:rounded-3xl overflow-hidden relative">
          {/* Mobile Navigation Handles */}
          <div className="sm:hidden absolute inset-y-0 left-0 flex items-center z-20 pl-1">
            <button
              onClick={() => handleServiceClick((activeService - 1 + services.length) % services.length)}
              className="mobile-nav-handle"
              aria-label="Previous slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <div className="sm:hidden absolute inset-y-0 right-0 flex items-center z-20 pr-1">
            <button
              onClick={() => handleServiceClick((activeService + 1) % services.length)}
              className="mobile-nav-handle"
              aria-label="Next slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {/* Add hidden scrollbar container for mobile */}
          <div className="sm:hidden absolute inset-0 overflow-hidden" aria-hidden="true"></div>

          {services.map((service, idx) => (
            <div 
              key={idx}
              onClick={() => handleServiceClick(idx)}
              className={`transition-all duration-500 ease-in-out rounded-2xl sm:rounded-3xl overflow-hidden relative flex-shrink-0 ${
                idx === activeService 
                  ? 'flex-grow' 
                  : 'hidden sm:block flex-shrink-0 w-16 md:w-24 cursor-pointer'
              }`}
            >
              {/* Background image */}
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${service.image})` }}
              ></div>
              
              <div className="carousel-overlay"></div>
              
              {idx === activeService ? (
                // Active section content
                <div className="relative h-full w-full flex flex-col justify-end px-2 py-4 sm:p-8 z-10 text-white">
                  <div className="w-full max-w-[95%] sm:max-w-lg text-left overflow-hidden">
                    <h3 className="service-title text-lg sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-4 truncate" style={{...whiteTextStyle, wordWrap: 'break-word' as const}}>{service.title}</h3>
                    <p className="service-description hidden sm:block text-sm md:text-base lg:text-lg mb-0 sm:mb-4 md:mb-6 leading-tight sm:leading-normal" style={{...whiteTextStyle, maxWidth: '100%', wordWrap: 'break-word' as const}}>{service.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0">
                    {/* Bottom left icon with theme color background */}
                    <div className="icon-circle-theme rounded-full p-2 sm:p-3 flex items-center justify-center" 
                         style={{ 
                           backgroundColor: themeColor2,
                           border: `1px solid ${getThemes?.txtColor || "#000000"}`
                         }}>
                      <img src={service.icon} alt="" className="w-4 h-4 sm:w-6 sm:h-6" 
                           style={{ filter: 'brightness(0) invert(1)' }} />
                    </div>
                    
                    {/* Bottom right enquire now button */}
                    <button
                      onClick={(e) => handleEnquire(service.productId, e)}
                      className="w-auto py-1 sm:py-2 px-3 sm:px-6 rounded-md cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105 text-xs sm:text-base"
                      style={{
                        background: themeColor2,
                        color: isWhiteOrShade(themeColor2) ? "#000000" : "#ffffff",
                        border: `1px solid ${getThemes?.txtColor || "#000000"}`,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = isWhiteOrShade(getThemes?.color1 || "") ? "black" : getThemes?.color1 || "";
                        e.currentTarget.style.color = isWhiteOrShade(getThemes?.color1 || "") ? "#ffffff" : getThemes?.txtColor || "#ffffff";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = themeColor2;
                        e.currentTarget.style.color = isWhiteOrShade(themeColor2) ? "#000000" : "#ffffff";
                      }}
                    >
                      Enquire Now
                    </button>
                  </div>
                </div>
              ) : (
                // Collapsed section with vertical text and icon (desktop only)
                <div className="h-full w-full flex flex-col items-center relative z-10">
                  <div 
                    className="collapsed-name font-bold text-sm sm:text-xl transform -rotate-90 whitespace-nowrap mt-12 sm:mt-24"
                    style={{
                      color: whiteTextStyle.color,
                      textShadow: whiteTextStyle.textShadow,
                      maxWidth: whiteTextStyle.maxWidth,
                      overflowWrap: 'break-word' as const,
                      wordWrap: 'break-word' as const
                    }}
                  >
                    {service.name}
                  </div>
                  {/* Bottom icon with theme color background */}
                  <div className="absolute bottom-4 sm:bottom-8 icon-circle-theme rounded-full p-2 sm:p-3 flex items-center justify-center" 
                       style={{ 
                         backgroundColor: themeColor2,
                         border: `1px solid ${getThemes?.txtColor || "#000000"}`
                       }}>
                    <img src={service.icon} alt="" className="w-4 h-4 sm:w-6 sm:h-6" 
                         style={{ filter: 'brightness(0) invert(1)' }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Bottom Navigation Dots */}
        <div className="flex justify-center mt-4 space-x-2">
            {services.map((_, idx) => (
                <button
                key={idx}
                onClick={() => handleServiceClick(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                    idx === activeService 
                    ? 'nav-dot-active' 
                    : 'bg-gray-400 hover:bg-gray-300'
                }`}
                />
            ))}
            </div>
      </div>
      
      {/* Feature Enquiry Modal */}
      <FeatureEnquiryModal 
        isOpen={isFeatureModalOpen} 
        onClose={() => setIsFeatureModalOpen(false)} 
        productId={selectedProductId}
      />
    </div>
  );
};

export default PropertyServicesCarousel;